/**
 * LeadIntakeUploadPage — public magic-link upload portal for leads
 * (Phase 5i, Jun 11 2026 — Ana spec §8, Daniel's addendum).
 *
 * Lives OUTSIDE the authenticated app shell (mirrors PublicProposalPage):
 * App.js detects /intake/<token> before the auth check and renders this
 * page directly. No sidebar, no session — the link IS the auth.
 *
 * Flow: resolve token context → lead picks files (PDF/JPEG/PNG/TIFF,
 * ≤25 MB each) + a category per file + an optional note → each file
 * streams browser → S3 via a presigned PUT (never touches our server) →
 * confirm writes the manifest row and notifies the physicians.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, UploadCloud, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API = '/api/public/lead-intake';

async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function sha256Hex(file) {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (_) { return null; }
}

export default function LeadIntakeUploadPage({ token }) {
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState('');
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]); // { file, category, note, status, error }
  const [uploading, setUploading] = useState(false);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const r = await post('/context', { token });
        if (gone) return;
        if (r && !r.error) setCtx(r);
        else setCtxError(r?.code === 'EXPIRED' ? 'This upload link has expired. Please contact your care team for a new one.'
          : r?.code === 'REVOKED' ? 'This upload link is no longer active. Please contact your care team.'
          : 'This upload link is not valid.');
      } catch (_) {
        if (!gone) setCtxError('Unable to reach the server. Please try again.');
      } finally { if (!gone) setLoading(false); }
    })();
    return () => { gone = true; };
  }, [token]);

  const addFiles = useCallback((fileList) => {
    const defaults = ctx?.categories?.[0] || 'other';
    const items = [...fileList].map((file) => ({
      file, category: defaults, note: '', status: 'ready', error: '',
    }));
    setQueue((q) => [...q, ...items].slice(0, 25));
  }, [ctx]);

  const setItem = (i, patch) => setQueue((q) => q.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setQueue((q) => q.filter((_, j) => j !== i));

  async function uploadAll() {
    setUploading(true);
    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      if (item.status === 'done') continue;
      setItem(i, { status: 'uploading', error: '' });
      try {
        const presign = await post('/presign', {
          token,
          filename: item.file.name,
          contentType: item.file.type || 'application/pdf',
          sizeBytes: item.file.size,
          category: item.category,
        });
        if (presign.error) throw new Error(presign.message || presign.code);

        const put = await fetch(presign.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': item.file.type || 'application/pdf' },
          body: item.file,
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);

        const hash = await sha256Hex(item.file);
        const confirm = await post('/confirm', {
          token,
          s3Key: presign.s3Key,
          sha256: hash,
          sizeBytes: item.file.size,
          category: item.category,
          filename: item.file.name,
          contentType: item.file.type || 'application/pdf',
          patientNote: item.note || null,
        });
        if (confirm.error) throw new Error(confirm.message || confirm.code);

        setItem(i, { status: 'done' });
        setDoneCount((n) => n + 1);
      } catch (e) {
        setItem(i, { status: 'error', error: e.message || 'Upload failed' });
      }
    }
    setUploading(false);
  }

  // ── Render states ──────────────────────────────────────────────────────
  if (loading) {
    return <Shell><p style={{ color: '#6C757D', textAlign: 'center' }}>Checking your secure link…</p></Shell>;
  }
  if (ctxError) {
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={36} color="#DC2626" style={{ marginBottom: 12 }} />
          <h2 style={{ margin: '0 0 8px', color: '#111827' }}>Link unavailable</h2>
          <p style={{ color: '#6C757D' }}>{ctxError}</p>
        </div>
      </Shell>
    );
  }

  const pending = queue.filter((q) => q.status !== 'done').length;
  const labels = ctx.categoryLabels || {};

  return (
    <Shell wide>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <ShieldCheck size={34} color="#2563eb" style={{ marginBottom: 8 }} />
        <h1 style={{ margin: '0 0 6px', fontSize: '1.45rem', color: '#111827' }}>
          Upload your medical records for ZignaGenix
        </h1>
        <p style={{ margin: 0, color: '#4B5563', fontSize: '0.95rem', maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
          {ctx.firstName ? `Hi ${ctx.firstName} — your` : 'Your'} physician will review these before your
          consultation, so the conversation can focus on you, not paperwork. Files are transmitted and
          stored encrypted.
        </p>
        {ctx.instructions && (
          <div style={{
            margin: '12px auto 0', maxWidth: 560, padding: '10px 14px', textAlign: 'left',
            background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10,
            fontSize: '0.85rem', color: '#1E40AF',
          }}>
            <strong>From your care team:</strong> {ctx.instructions}
          </div>
        )}
      </div>

      {/* File picker */}
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        padding: '28px 16px', border: '2px dashed #93C5FD', borderRadius: 14,
        background: '#F8FAFC', cursor: 'pointer', marginBottom: 16,
      }}>
        <UploadCloud size={28} color="#2563eb" />
        <span style={{ fontWeight: 700, color: '#1F2937', fontSize: '0.95rem' }}>
          Choose files or drag them here
        </span>
        <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
          PDF, JPEG, PNG, or TIFF — up to 25 MB each
        </span>
        <input
          type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/jpeg,image/png,image/tiff"
          style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </label>

      {/* Queue */}
      {queue.map((item, i) => (
        <div key={`${item.file.name}-${i}`} style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: 10,
          marginBottom: 8, background: item.status === 'done' ? '#F0FDF4' : '#fff',
        }}>
          <FileText size={16} color="#6B7280" />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', wordBreak: 'break-all' }}>
              {item.file.name}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
              {(item.file.size / (1024 * 1024)).toFixed(1)} MB
            </div>
            {item.error && <div style={{ fontSize: '0.75rem', color: '#DC2626' }}>{item.error}</div>}
          </div>
          {item.status === 'done' ? (
            <span style={{ color: '#15803D', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
              <CheckCircle2 size={15} /> Uploaded
            </span>
          ) : (
            <>
              <select
                value={item.category}
                onChange={(e) => setItem(i, { category: e.target.value })}
                disabled={uploading}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: '0.8rem' }}
              >
                {(ctx.categories || []).map((c) => <option key={c} value={c}>{labels[c] || c}</option>)}
              </select>
              <input
                type="text" placeholder="Note (optional)"
                value={item.note}
                onChange={(e) => setItem(i, { note: e.target.value })}
                disabled={uploading}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: '0.8rem', width: 150 }}
              />
              <button onClick={() => removeItem(i)} disabled={uploading} style={{
                background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '0.8rem',
              }}>
                Remove
              </button>
            </>
          )}
        </div>
      ))}

      {queue.length > 0 && pending > 0 && (
        <button onClick={uploadAll} disabled={uploading} style={{
          width: '100%', padding: '0.8rem', marginTop: 8,
          background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10,
          fontSize: '1rem', fontWeight: 700, cursor: uploading ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {uploading
            ? <><Loader2 size={16} className="spin" /> Uploading securely…</>
            : `Upload ${pending} file${pending > 1 ? 's' : ''}`}
        </button>
      )}

      {doneCount > 0 && pending === 0 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <CheckCircle2 size={30} color="#15803D" style={{ marginBottom: 8 }} />
          <h3 style={{ margin: '0 0 4px', color: '#111827' }}>All set — thank you!</h3>
          <p style={{ margin: 0, color: '#6B7280', fontSize: '0.9rem' }}>
            Your records were uploaded securely. Your physician will review them before your consultation.
            You can close this page, or add more files above.
          </p>
        </div>
      )}

      <p style={{ marginTop: 24, textAlign: 'center', fontSize: '0.72rem', color: '#9CA3AF' }}>
        ZignaGenix Regenerative Medicine · Files are encrypted in transit and at rest · This secure link
        expires {ctx.expiresAt ? new Date(ctx.expiresAt).toLocaleDateString() : 'after a limited time'}.
      </p>
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', padding: '2rem 1rem' }}>
      <div style={{
        maxWidth: wide ? 680 : 460, margin: '0 auto', background: '#fff',
        borderRadius: 16, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        {children}
      </div>
    </div>
  );
}
